import { SkeletonBrick } from '@consta/uikit/Skeleton'
import type { CubeInfo, MarkerNamesByField, WellBrief, WellsByField } from '@core/core/types'
import {
  IconFolderWell,
  IconGeoObjectMarkers,
  IconGeoObjectWell,
  IconGeoObjectWells,
  IconGeoObjSeismic,
  IconGeoObjSeismic3D,
} from '@core/icons'

import { CustomLabel } from '../ProjectObjects'
import {
  cubeNode,
  cubesRoot,
  dummyCount,
  dummyNode,
  fieldNode,
  markerNode,
  markersRoot,
  rootNode,
  wellNode,
  wellsRoot,
} from './constants'
import css from './GeoDbImport.css'
import type { TypedDataNode } from './types'

type Props = {
  fieldsWithWells: WellsByField[]
  fieldsWithMarkers: MarkerNamesByField[]
  filter: string
  wellsLimit: number
  cubes: CubeInfo[]
}

const getObjectTreeNodeName = (name: string) => ({ name })

function getDummyNodes(path: string, count: number): TypedDataNode[] {
  return Array.from({ length: count }, (_, i) => ({
    selectable: false,
    checkable: false,
    key: `${path}/${dummyNode}#${i + 1}`,
    id: dummyNode,
    type: dummyNode,
    title: (
      <span className={css.skeletonWrapper}>
        <SkeletonBrick className={css.skeleton} height={10} width={100} />{' '}
      </span>
    ),
  }))
}

const wellIconColor = { color: '#32B4FF' }

function getWellNodes(
  path: string,
  wells: WellBrief[],
  filter: (well: WellBrief) => boolean,
  wellsLimit: number,
): TypedDataNode[] {
  return wells
    .filter(filter)
    .slice(0, wellsLimit)
    .map(({ id, name }) => ({
      isLeaf: true,
      selectable: false,
      key: `${path}/${id}`,
      id,
      type: wellNode,
      title: (
        <CustomLabel
          objectTreeNode={getObjectTreeNodeName(name)}
          icon={<IconGeoObjectWell style={wellIconColor} />}
        />
      ),
    }))
}

function getWellFieldNodes(
  path: string,
  fieldsWithWells: WellsByField[],
  filter: (well: WellBrief) => boolean,
  wellsLimit: number,
): TypedDataNode[] {
  return fieldsWithWells.map(field => {
    const { id, name, wells } = field
    const fieldPath = `${path}/${id}`
    return {
      isLeaf: false,
      selectable: false,
      checkable: wells !== undefined,
      disableCheckbox: !wells?.some(filter),
      key: fieldPath,
      id,
      type: fieldNode,
      title: (
        <CustomLabel
          objectTreeNode={getObjectTreeNodeName(name)}
          icon={<IconFolderWell size="s" />}
        />
      ),
      children:
        wells !== undefined
          ? getWellNodes(fieldPath, wells, filter, wellsLimit)
          : getDummyNodes(fieldPath, dummyCount),
    }
  })
}

function getWellsSubTree(
  fieldsWithWells: WellsByField[],
  filter: string,
  wellsLimit: number,
): TypedDataNode {
  const wellFilter = (well: WellBrief) => filter === '' || well.name.toLowerCase().includes(filter)

  return {
    checkable: false,
    selectable: false,
    isLeaf: false,
    key: wellsRoot,
    id: wellsRoot,
    type: rootNode,
    title: (
      <CustomLabel
        objectTreeNode={getObjectTreeNodeName('Скважины')}
        icon={<IconGeoObjectWells size="s" />}
      />
    ),
    children:
      fieldsWithWells.length > 0
        ? getWellFieldNodes(wellsRoot, fieldsWithWells, wellFilter, wellsLimit)
        : getDummyNodes(wellsRoot, dummyCount),
  }
}

function getMarkerNodes(
  path: string,
  markerNames: string[],
  filter: (markerName: string) => boolean,
): TypedDataNode[] {
  return markerNames.filter(filter).map(markerName => {
    return {
      isLeaf: true,
      selectable: false,
      key: `${path}/${markerName}`,
      id: markerName,
      type: markerNode,
      title: (
        <CustomLabel
          objectTreeNode={getObjectTreeNodeName(markerName)}
          icon={<IconGeoObjectMarkers size="s" />}
        />
      ),
      children: [],
    }
  })
}

function getMarkerFieldNodes(
  path: string,
  fieldsWithMarkers: MarkerNamesByField[],
  filter: (markerName: string) => boolean,
): TypedDataNode[] {
  return fieldsWithMarkers.map(field => {
    const { id, name, markerNames } = field
    const fieldPath = `${path}/${id}`
    return {
      isLeaf: false,
      selectable: false,
      checkable: markerNames !== undefined,
      disableCheckbox: !markerNames?.some(filter),
      key: fieldPath,
      id,
      type: fieldNode,
      title: (
        <CustomLabel
          objectTreeNode={getObjectTreeNodeName(name)}
          icon={<IconFolderWell size="s" />}
        />
      ),
      children:
        markerNames !== undefined
          ? getMarkerNodes(fieldPath, markerNames, filter)
          : getDummyNodes(fieldPath, dummyCount),
    }
  })
}

function getMarkersSubTree(fieldsWithMarkers: MarkerNamesByField[], filter: string): TypedDataNode {
  const markerFilter = (markerName: string) =>
    filter === '' || markerName.toLowerCase().includes(filter)

  return {
    selectable: false,
    checkable: false,
    isLeaf: false,
    key: markersRoot,
    id: markersRoot,
    type: rootNode,
    title: (
      <CustomLabel
        objectTreeNode={getObjectTreeNodeName('Маркеры')}
        icon={<IconGeoObjectMarkers size="s" />}
      />
    ),
    children:
      fieldsWithMarkers.length > 0
        ? getMarkerFieldNodes(markersRoot, fieldsWithMarkers, markerFilter)
        : getDummyNodes(markersRoot, dummyCount),
  }
}

function getCubesSubTree(cubes: CubeInfo[], filter: string): TypedDataNode {
  const cubeFilter = (cube: CubeInfo) => filter === '' || cube.name.toLowerCase().includes(filter)

  return {
    isLeaf: false,
    selectable: false,
    checkable: false,
    key: cubesRoot,
    id: cubesRoot,
    type: rootNode,
    title: (
      <CustomLabel
        objectTreeNode={getObjectTreeNodeName('Сейсмика')}
        icon={<IconGeoObjSeismic size="s" />}
      />
    ),
    children: cubes.filter(cubeFilter).map(cube => {
      return {
        isLeaf: true,
        selectable: false,
        key: `${cubesRoot}/${cube.id}`,
        id: cube.id,
        type: cubeNode,
        title: (
          <CustomLabel
            objectTreeNode={getObjectTreeNodeName(cube.name)}
            icon={<IconGeoObjSeismic3D size="s" />}
          />
        ),
        children: [],
      }
    }),
  }
}

export function getTreeData({
  fieldsWithWells,
  fieldsWithMarkers,
  filter,
  wellsLimit = 100,
  cubes,
}: Props): TypedDataNode[] {
  const normFilter = filter.trim().toLowerCase()

  return [
    getWellsSubTree(fieldsWithWells, normFilter, wellsLimit),
    getMarkersSubTree(fieldsWithMarkers, normFilter),
    getCubesSubTree(cubes, normFilter),
  ]
}
